import { useState, useEffect, useMemo } from "react";
import { adminFetchOrders, adminUpdateOrder } from "@/lib/api";

interface OrderItem {
  id: string;
  name: string;
  price: number;
  size: string;
  color: string;
  quantity: number;
  image: string;
}

interface Order {
  id: string;
  orderNumber: string;
  status: string;
  email: string;
  firstName: string;
  lastName: string;
  city: string;
  state: string;
  country: string;
  subtotal: number;
  shippingCost: number;
  promoDiscount: number;
  tax: number;
  total: number;
  shippingMethod: string;
  items: OrderItem[];
  createdAt: string;
}

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending", color: "bg-yellow-50 text-yellow-700" },
  { value: "confirmed", label: "Confirmed", color: "bg-blue-50 text-blue-700" },
  { value: "processing", label: "Processing", color: "bg-purple-50 text-purple-700" },
  { value: "shipped", label: "Shipped", color: "bg-cyan-50 text-cyan-700" },
  { value: "delivered", label: "Delivered", color: "bg-emerald-50 text-emerald-700" },
  { value: "cancelled", label: "Cancelled", color: "bg-red-50 text-red-700" },
];

export default function OrdersPanel() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    adminFetchOrders()
      .then((data) => setOrders(data as Order[]))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (filterStatus === "all") return orders;
    return orders.filter((o) => o.status === filterStatus);
  }, [orders, filterStatus]);

  const handleStatusChange = async (orderId: string, newStatus: string) => {
    try {
      await adminUpdateOrder(orderId, newStatus);
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, status: newStatus } : o))
      );
    } catch (err) {
      console.error("Failed to update order:", err);
    }
  };

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    orders.forEach((o) => { counts[o.status] = (counts[o.status] || 0) + 1; });
    return counts;
  }, [orders]);

  const totalRevenue = useMemo(() => orders.reduce((sum, o) => sum + o.total, 0), [orders]);

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="animate-pulse bg-white rounded-xl p-6">
            <div className="bg-[#f0f0f0] h-4 w-1/3 mb-3 rounded" />
            <div className="bg-[#f0f0f0] h-3 w-1/2 rounded" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total Orders", value: orders.length, icon: "ri-file-list-3-line" },
          { label: "Pending", value: statusCounts["pending"] || 0, icon: "ri-time-line", color: "text-yellow-600" },
          { label: "Shipped", value: statusCounts["shipped"] || 0, icon: "ri-truck-line", color: "text-cyan-600" },
          { label: "Revenue", value: `$${totalRevenue.toFixed(2)}`, icon: "ri-money-dollar-circle-line", color: "text-emerald-600" },
        ].map((stat) => (
          <div key={stat.label} className="bg-[#fafaf9] rounded-xl p-4 flex items-center gap-4">
            <div className={`w-9 h-9 flex items-center justify-center rounded-full bg-white ${stat.color ?? "text-[#555]"}`}>
              <i className={`${stat.icon} text-lg`}></i>
            </div>
            <div>
              <div className="text-2xl font-light text-[#1a1a1a]">{stat.value}</div>
              <div className="text-xs text-[#aaa]">{stat.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Status filter */}
      <div className="flex gap-1 bg-[#f5f5f3] rounded-lg p-1 mb-5 w-fit">
        <button
          onClick={() => setFilterStatus("all")}
          className={`px-3 py-1.5 rounded-md text-xs cursor-pointer transition-colors whitespace-nowrap ${
            filterStatus === "all" ? "bg-white text-[#1a1a1a] shadow-sm" : "text-[#888] hover:text-[#555]"
          }`}
        >
          All ({orders.length})
        </button>
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.value}
            onClick={() => setFilterStatus(s.value)}
            className={`px-3 py-1.5 rounded-md text-xs cursor-pointer transition-colors whitespace-nowrap ${
              filterStatus === s.value ? "bg-white text-[#1a1a1a] shadow-sm" : "text-[#888] hover:text-[#555]"
            }`}
          >
            {s.label} ({statusCounts[s.value] || 0})
          </button>
        ))}
      </div>

      {/* Orders list */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-[#bbb]">
          <i className="ri-inbox-line text-4xl mb-3 block" />
          <p className="text-sm">No orders found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((order) => {
            const statusInfo = STATUS_OPTIONS.find((s) => s.value === order.status) || STATUS_OPTIONS[0];
            const isExpanded = expandedId === order.id;

            return (
              <div key={order.id} className="bg-white border border-[#f0f0f0] rounded-xl overflow-hidden">
                {/* Order header */}
                <div
                  className="px-5 py-4 flex items-center justify-between cursor-pointer hover:bg-[#fafaf9] transition-colors"
                  onClick={() => setExpandedId(isExpanded ? null : order.id)}
                >
                  <div className="flex items-center gap-5">
                    <div>
                      <div className="text-sm font-medium text-[#1a1a1a]">{order.orderNumber}</div>
                      <div className="text-xs text-[#bbb] mt-0.5">
                        {new Date(order.createdAt).toLocaleDateString("en-US", {
                          year: "numeric", month: "short", day: "numeric",
                        })}
                      </div>
                    </div>
                    <div className="text-sm text-[#555]">
                      {order.firstName} {order.lastName}
                    </div>
                    <div className="text-xs text-[#aaa]">{order.email}</div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-sm font-medium text-[#1a1a1a]">${order.total.toFixed(2)}</div>
                    <span className={`text-xs rounded-full px-2.5 py-1 ${statusInfo.color}`}>
                      {statusInfo.label}
                    </span>
                    <i className={`ri-arrow-${isExpanded ? "up" : "down"}-s-line text-[#bbb]`} />
                  </div>
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="border-t border-[#f0f0f0] px-5 py-4">
                    <div className="grid grid-cols-2 gap-6">
                      {/* Items */}
                      <div>
                        <h4 className="text-xs tracking-[0.1em] text-[#aaa] uppercase mb-3">Items ({order.items.length})</h4>
                        <div className="space-y-2">
                          {order.items.map((item) => (
                            <div key={item.id} className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg overflow-hidden bg-[#f5f5f5] flex-shrink-0">
                                {item.image && <img src={item.image} alt={item.name} className="w-full h-full object-cover" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm text-[#1a1a1a] truncate">{item.name}</div>
                                <div className="text-xs text-[#aaa]">{item.size} · {item.color} · Qty {item.quantity}</div>
                              </div>
                              <div className="text-sm text-[#1a1a1a]">${(item.price * item.quantity).toFixed(2)}</div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Info + Actions */}
                      <div>
                        <h4 className="text-xs tracking-[0.1em] text-[#aaa] uppercase mb-3">Shipping</h4>
                        <p className="text-sm text-[#555] mb-1">
                          {order.firstName} {order.lastName}
                        </p>
                        <p className="text-xs text-[#aaa] mb-4">
                          {order.city}, {order.state} · {order.country}
                        </p>

                        <h4 className="text-xs tracking-[0.1em] text-[#aaa] uppercase mb-3">Update Status</h4>
                        <select
                          value={order.status}
                          onChange={(e) => handleStatusChange(order.id, e.target.value)}
                          className="border border-[#e8e8e8] rounded-lg px-3 py-2 text-sm text-[#1a1a1a] outline-none focus:border-[#1a1a1a] cursor-pointer"
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </select>

                        <div className="mt-4 pt-4 border-t border-[#f5f5f5] space-y-1">
                          <div className="flex justify-between text-xs text-[#aaa]">
                            <span>Subtotal</span><span>${order.subtotal.toFixed(2)}</span>
                          </div>
                          {order.promoDiscount > 0 && (
                            <div className="flex justify-between text-xs text-emerald-600">
                              <span>Discount</span><span>-${order.promoDiscount.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-xs text-[#aaa]">
                            <span>Shipping</span><span>${order.shippingCost.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-xs text-[#aaa]">
                            <span>Tax</span><span>${order.tax.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-sm font-medium text-[#1a1a1a] pt-1">
                            <span>Total</span><span>${order.total.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
